import type { LeadForm, LeadFormText } from '../../../types/campaign';
import { PlaceholderHint } from './PlaceholderHint';

// Lead form authoring (spec §2.5). Each of the four text fields toggles
// between 'Generic' (canned Meta default, no text stored) and 'Custom' (free
// text with placeholders). Per the spec the text input is hidden when mode
// is Generic. Description/thankYouDescription are only *rendered* when their
// paired headline is Custom -- the UI surfaces that coupling by disabling
// the description mode switch when the paired headline is Generic.

type FieldKey = 'headline' | 'description' | 'thankYouHeadline' | 'thankYouDescription';

const FIELD_META: Record<FieldKey, { label: string; maxChars: number; dependsOn?: FieldKey }> = {
  headline: { label: 'Form headline', maxChars: 255 },
  description: { label: 'Form description', maxChars: 2000, dependsOn: 'headline' },
  thankYouHeadline: { label: 'Thank-you headline', maxChars: 255 },
  thankYouDescription: {
    label: 'Thank-you description',
    maxChars: 255,
    dependsOn: 'thankYouHeadline',
  },
};

function setMode(current: LeadFormText, mode: 'Generic' | 'Custom'): LeadFormText {
  if (mode === 'Generic') return { mode: 'Generic' };
  if (current.mode === 'Custom') return current;
  return { mode: 'Custom', text: '' };
}

export type LeadFormEditorProps = {
  value: LeadForm;
  onChange: (next: LeadForm) => void;
};

export function LeadFormEditor({ value, onChange }: LeadFormEditorProps) {
  const setField = (key: FieldKey, next: LeadFormText) => {
    onChange({ ...value, [key]: next });
  };

  const renderField = (key: FieldKey) => {
    const meta = FIELD_META[key];
    const current = value[key];
    const parentKey = meta.dependsOn;
    const parentMode = parentKey ? value[parentKey].mode : null;
    const locked = parentMode !== null && parentMode !== 'Custom';

    return (
      <div key={key} className="space-y-1">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-900 flex-1">{meta.label}</label>
          <select
            className="rounded-md border border-slate-300 px-2 py-1 text-sm disabled:opacity-50"
            value={current.mode}
            disabled={locked}
            onChange={(e) => setField(key, setMode(current, e.target.value as 'Generic' | 'Custom'))}
          >
            <option value="Generic">Generic</option>
            <option value="Custom">Custom</option>
          </select>
        </div>
        {locked && (
          <p className="text-xs text-slate-500">
            This field only renders when the paired headline is Custom.
          </p>
        )}
        {current.mode === 'Custom' && (
          <>
            <textarea
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={current.text}
              onChange={(e) =>
                setField(key, { mode: 'Custom', text: e.target.value })
              }
              placeholder={`Custom ${meta.label.toLowerCase()}`}
            />
            <div
              className={`text-xs ${
                current.text.length > meta.maxChars ? 'text-red-600' : 'text-slate-500'
              }`}
            >
              {current.text.length} / {meta.maxChars} characters
              {current.text.length > meta.maxChars ? ' — over the limit' : ''}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {(Object.keys(FIELD_META) as FieldKey[]).map(renderField)}
      <PlaceholderHint />
    </div>
  );
}
